import React, { FC, useEffect } from 'react';
import { Container, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCollections } from '../../../store/landscapeAPI/collection/reducers/collection.slice';
import {
  GoalCollectionSaveRequestDto,
  GoalCollectionSaveRequestDtoWithFlag,
} from '../../../store/landscapeAPI/goal/dto/request/goal-collection-save-request.dto';
import {
  GoalThemeSaveRequestDto,
  GoalThemeSaveRequestDtoWithFlag,
} from '../../../store/landscapeAPI/goal/dto/request/goal-theme-save-request.dto';
import { GoalResponseDto } from '../../../store/landscapeAPI/goal/dto/response/goal-response.dto';
import { GoalType } from '../../../store/landscapeAPI/goal/goal-type';
import {
  GoalIndicatorSaveRequestDtoWithFlag,
  IndicatorGoalSaveRequestDto,
} from '../../../store/landscapeAPI/indicator/dto/request/indicator-goal-save-request.dto';
import { fetchIndicators } from '../../../store/landscapeAPI/indicator/reducers/indicator.slice';
import { fetchThemes } from '../../../store/landscapeAPI/theme/reducers/theme.slice';
import { AppDispatch, RootState } from '../../../store/store';
import { getUUID } from '../../../utils/UUID';
import { Button, RadioButton } from '../../atoms';
import Spinner from '../../atoms/Spinner/Spinner';
import { CustomDropdown, MultiSelectWithFiltering } from '../../molecules';
import styles from './ReportingDetailsGoalsForm.module.css';

interface ReportingDetailsGoalsFormProps {
  data: GoalResponseDto;
  goalThemes: GoalThemeSaveRequestDto[];
  handleOnGoalThemesChange: (
    goalThemes: GoalThemeSaveRequestDtoWithFlag[]
  ) => void;
  goalCollections: GoalCollectionSaveRequestDto[];
  handleOnGoalCollectionsChange: (
    goalCollections: GoalCollectionSaveRequestDtoWithFlag[]
  ) => void;
  goalIndicators: IndicatorGoalSaveRequestDto[];
  handleOnGoalIndicatorsChange: (
    goalIndicators: GoalIndicatorSaveRequestDtoWithFlag[]
  ) => void;
  callbackFn: (goal: GoalResponseDto) => void;
  handleOnChange: (goal: GoalResponseDto) => void;
}

const ReportingDetailsGoalsForm: FC<ReportingDetailsGoalsFormProps> = ({
  data,
  goalThemes,
  handleOnGoalThemesChange,
  goalCollections,
  handleOnGoalCollectionsChange,
  goalIndicators,
  handleOnGoalIndicatorsChange,
  callbackFn,
  handleOnChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { themes, status: themeStatus } = useSelector(
    (state: RootState) => state.themeSlice
  );
  const { collections, status: collectionStatus } = useSelector(
    (state: RootState) => state.collectionSlice
  );
  const { indicators, status: indicatorStatus } = useSelector(
    (state: RootState) => state.indicatorSlice
  );

  useEffect(() => {
    dispatch(fetchThemes());
    dispatch(fetchCollections());
    dispatch(fetchIndicators());
  }, [dispatch]);

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    callbackFn(data);
  };

  if (
    themeStatus === 'loading' ||
    collectionStatus === 'loading' ||
    indicatorStatus === 'loading'
  ) {
    return <Spinner />;
  }

  return (
    <>
      <Container className={styles.form}>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={data.name}
              onChange={(e) =>
                handleOnChange({
                  ...data,
                  name: e.target.value,
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Goal type</Form.Label>
            <CustomDropdown
              initialTitle={data?.type?.toString() ?? ''}
              items={[
                {
                  eventKey: GoalType.Core.toString(),
                  label: 'Core',
                },
                {
                  eventKey: GoalType.Local.toString(),
                  label: 'Local',
                },
              ]}
              onSelectOption={(dropdownItem) => {
                const type =
                  dropdownItem.eventKey === GoalType.Core.toString()
                    ? GoalType.Core
                    : GoalType.Local;
                handleOnChange({
                  ...data,
                  type,
                });
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Themes</Form.Label>
            <MultiSelectWithFiltering
              name="themes"
              inputId="themes"
              options={
                themes?.map((theme) => ({
                  label: theme.name,
                  value: theme.id,
                })) ?? []
              }
              defaultValues={
                data.themes?.map((theme) => ({
                  label: theme.name,
                  value: theme.id,
                })) || []
              }
              onSelect={(selectedValues) => {
                const selected = selectedValues.map((theme) => ({
                  id:
                    goalThemes.find(
                      (goalTheme) => goalTheme.themeId === theme.value
                    )?.id ?? getUUID(),
                  themeId: theme.value,
                  published: true,
                  goalId: data.id,
                }));

                const toBeDeleted = goalThemes.filter(
                  (goalTheme) =>
                    !selected.some(
                      (theme) => theme.themeId === goalTheme.themeId
                    )
                );

                const toBeAdded = selected.filter(
                  (goalTheme) =>
                    !goalThemes.some(
                      (theme) => theme.themeId === goalTheme.themeId
                    )
                );

                handleOnGoalThemesChange([
                  ...toBeDeleted.map((item) => ({
                    ...item,
                    toBeDeleted: true,
                  })),
                  ...toBeAdded.map((item) => ({
                    ...item,
                    toBeDeleted: false,
                  })),
                ]);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Collections</Form.Label>
            <MultiSelectWithFiltering
              name="collections"
              inputId="collections"
              options={
                collections?.map((collection) => ({
                  label: collection.name,
                  value: collection.id,
                })) ?? []
              }
              defaultValues={
                data.collections?.map((collection) => ({
                  label: collection.name,
                  value: collection.id,
                })) || []
              }
              onSelect={(selectedValues) => {
                const selected = selectedValues.map((collection) => ({
                  id:
                    goalCollections.find(
                      (goalCollection) =>
                        goalCollection.collectionId === collection.value
                    )?.id ?? getUUID(),
                  collectionId: collection.value,
                  published: true,
                  goalId: data.id,
                }));

                const toBeDeleted = goalCollections.filter(
                  (goalCollection) =>
                    !selected.some(
                      (collection) =>
                        collection.collectionId === goalCollection.collectionId
                    )
                );

                const toBeAdded = selected.filter(
                  (goalCollection) =>
                    !goalCollections.some(
                      (collection) =>
                        collection.collectionId === goalCollection.collectionId
                    )
                );

                handleOnGoalCollectionsChange([
                  ...toBeDeleted.map((item) => ({
                    ...item,
                    toBeDeleted: true,
                  })),
                  ...toBeAdded.map((item) => ({
                    ...item,
                    toBeDeleted: false,
                  })),
                ]);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Indicators</Form.Label>
            <MultiSelectWithFiltering
              name="indicators"
              inputId="indicators"
              options={
                indicators?.map((indicator) => ({
                  label: indicator.name,
                  value: indicator.id,
                })) ?? []
              }
              defaultValues={
                data.indicators?.map((indicator) => ({
                  label: indicator.name,
                  value: indicator.id,
                })) || []
              }
              onSelect={(selectedValues) => {
                const selected = selectedValues.map((indicator) => ({
                  id:
                    goalIndicators.find(
                      (goalIndicator) =>
                        goalIndicator.indicatorId === indicator.value
                    )?.id ?? getUUID(),
                  indicatorId: indicator.value,
                  published: true,
                  goalId: data.id,
                }));

                const toBeDeleted = goalIndicators.filter(
                  (goalIndicator) =>
                    !selected.some(
                      (indicator) =>
                        indicator.indicatorId === goalIndicator.indicatorId
                    )
                );

                const toBeAdded = selected.filter(
                  (goalIndicator) =>
                    !goalIndicators.some(
                      (indicator) =>
                        indicator.indicatorId === goalIndicator.indicatorId
                    )
                );

                handleOnGoalIndicatorsChange([
                  ...toBeDeleted.map((item) => ({
                    ...item,
                    toBeDeleted: true,
                  })),
                  ...toBeAdded.map((item) => ({
                    ...item,
                    toBeDeleted: false,
                  })),
                ]);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Is Published</Form.Label>
            <br />
            <RadioButton
              defaultChecked={data.published}
              onChange={(checked) =>
                handleOnChange({
                  ...data,
                  published: checked,
                })
              }
            />
          </Form.Group>
          <Button variant={'dark'} onClick={handleSubmit}>
            Save
          </Button>
        </Form>
      </Container>
    </>
  );
};

export default ReportingDetailsGoalsForm;
